import type { FC } from 'react';
import React from 'react';
import { alpha, Box, CircularProgress, Typography } from '@mui/material';
import type { BudgetAlertSeverity } from '@pflegenavi/frontend/api-nursing-home';
import { useFormatting } from '@pflegenavi/frontend/localization';

const SEVERITY_COLOR: Record<BudgetAlertSeverity, string> = {
  info: 'info.main',
  warning: 'warning.main',
  error: 'error.main',
};

/**
 * MUI palette path for a budget's status: red when over 100%, otherwise the
 * highest active alert severity, otherwise green. Single source of truth for
 * both the usage ring and the dashboard row accent.
 */
export const budgetIndicatorColor = (
  percent: number,
  severity?: BudgetAlertSeverity | null
): string =>
  percent > 100
    ? 'error.main'
    : severity
    ? SEVERITY_COLOR[severity]
    : 'success.main';

interface BudgetUsageCircleProps {
  /** Usage in percent; may exceed 100. The ring caps at 100; the label shows the rounded value, or ">100%" when non-finite. */
  percent: number;
  /**
   * Severity of the most severe active threshold alert, if any. Colors the ring
   * below 100%; being over 100% always shows red regardless of severity.
   */
  severity?: BudgetAlertSeverity | null;
  size?: number;
}

export const BudgetUsageCircle: FC<BudgetUsageCircleProps> = ({
  percent,
  severity,
  size = 48,
}) => {
  const label = Number.isFinite(percent) ? `${Math.round(percent)}` : '>100';
  const ringColor = budgetIndicatorColor(percent, severity);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{ color: 'grey.50016' }}
      />
      <CircularProgress
        variant="determinate"
        value={Math.min(percent, 100)}
        size={size}
        thickness={4}
        sx={{
          color: ringColor,
          position: 'absolute',
          left: 0,
        }}
      />
      <Box
        sx={{
          inset: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: (theme) => {
            const group = ringColor.split('.')[0] as
              | 'error'
              | 'warning'
              | 'info'
              | 'success';
            return alpha(theme.palette[group].main, 0.12);
          },
        }}
      >
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ fontWeight: 700 }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

interface BudgetAvailableAmountProps {
  availableCents: number;
}

export const BudgetAvailableAmount: FC<BudgetAvailableAmountProps> = ({
  availableCents,
}) => {
  const { fCurrencyCents } = useFormatting();
  return (
    <Typography
      variant="subtitle2"
      color={availableCents >= 0 ? 'success.main' : 'error.main'}
    >
      {fCurrencyCents(availableCents)}
    </Typography>
  );
};
